/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect } from "react";

import Link from "next/link";
import { useRouter } from "next/router";
import { isElectron } from "react-device-detect";
import { connectHighlight } from "react-instantsearch-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { ReturnIcon } from "src/components/Search/Hits/Icons";
import { Avatar } from "src/components/Shared/User/Avatar";
import { SearchHit } from "src/lib/types";
import { AppState } from "src/store";
import { searchActions } from "src/store/modules/search";

import {
  SearchResultsItem,
  StyledLink,
  ResultRight,
  ResultLeft,
  ResultIconLeft,
  ResultContentWrapper,
} from "./HitComponents";
import { HitIcon } from "./HitIcon";

type HighlightProps = {
  hit: SearchHit;
  highlight: any;
};

/**
 * A custom highlight component, see https://www.algolia.com/doc/api-reference/widgets/highlight/react/
 *
 * @param props - the props sent to highlight display preview
 * @returns
 */
const Highlight = ({ highlight, ...props }: HighlightProps) => {
  const hit = props.hit;
  switch (hit.recordType) {
    case "user":
      const parsedHitUsername = highlight({
        highlightProperty: "_highlightResult",
        attribute: "username",
        hit,
      });

      const parsedHitDisplayName = highlight({
        highlightProperty: "_highlightResult",
        attribute: "displayName",
        hit,
      });

      const parsedHitUserTitle = highlight({
        highlightProperty: "_highlightResult",
        attribute: "page.title",
        hit,
      });

      const hitsInPage = [...parsedHitUserTitle].some((part: any) => part.isHighlighted);

      return (
        <>
          <FirstLine>
            @
            {parsedHitUsername.map((part: any, index: any) =>
              part.isHighlighted ? <mark key={index}>{part.value}</mark> : <span key={index}>{part.value}</span>
            )}
          </FirstLine>
          <SecondLine>
            {parsedHitDisplayName.map((part: any, index: any) =>
              part.isHighlighted ? <mark key={index}>{part.value}</mark> : <span key={index}>{part.value}</span>
            )}{" "}
            {hit?.pages?.length > 0 && (
              <>
                {hit.displayName && "--  "}
                {`${hit?.pages?.length} pages`}
              </>
            )}
          </SecondLine>
          {hitsInPage && (
            <span>
              <br />
              {parsedHitUserTitle.map((part: any, index: any) =>
                part.isHighlighted ? <mark key={index}>{part.value}</mark> : <span key={index}>{part.value}</span>
              )}
            </span>
          )}
        </>
      );
    case "page":
      const parsedTitle = highlight({
        highlightProperty: "_highlightResult",
        attribute: "title",
        hit,
      });
      const parsedHitPageUsername = highlight({
        highlightProperty: "_highlightResult",
        attribute: "user.username",
        hit,
      });
      const parsedHitPageDisplayName = highlight({
        highlightProperty: "_highlightResult",
        attribute: "user.displayName",
        hit,
      });
      const hitsInUser = [...parsedHitPageUsername, ...parsedHitPageDisplayName].some(
        (part: any) => part.isHighlighted
      );
      return (
        <>
          <FirstLine>
            {parsedTitle.map((part: any, index: any) =>
              part.isHighlighted ? <mark key={index}>{part.value}</mark> : <span key={index}>{part.value}</span>
            )}
          </FirstLine>
          <SecondLine>
            {hitsInUser ? (
              <span>
                @
                {parsedHitPageUsername.map((part: any, index: any) =>
                  part.isHighlighted ? (
                    <mark key={index}>{part.value}</mark>
                  ) : (
                    <span key={index}>{part.value}</span>
                  )
                )}
              </span>
            ) : (
              <>@{hit.user.username}</>
            )}
          </SecondLine>
        </>
      );
    default:
      return <></>;
  }
};

const CustomHighlight = connectHighlight(Highlight);

const FirstLine = styled.div``;

const SecondLine = styled.div`
  color: #a0a0a0;
  font-size: 12px;
  height: 24px;
  :hover {
    text-decoration: none;
  }
`;

interface Props {
  hit: SearchHit;
  enableKeyboardNav?: boolean;
}

export const Hit = ({ hit, enableKeyboardNav }: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const focusedResultIndex = useSelector((state: AppState) => state.search.focusedResultIndex);
  const goToFocused = useSelector((state: AppState) => state.search.goToFocused);

  const url = () => {
    switch (hit.recordType) {
      case "user":
        return `/user/${hit.username}`;
      case "page":
        return `/plate/${hit.user.username}/${encodeURIComponent(hit.title)}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    if (focusedResultIndex === hit.__position && goToFocused) {
      router.push(url(), undefined, { shallow: isElectron ? false : true });
      dispatch(searchActions.searchModalClosed({}));
    }
  }, [goToFocused]);

  return (
    <SearchResultsItem focused={enableKeyboardNav && focusedResultIndex === hit.__position}>
      <Link href={url()} passHref>
        <StyledLink onClick={() => dispatch(searchActions.searchModalClosed({}))}>
          <ResultLeft>
            <ResultIconLeft>
              {hit.recordType === "user" ? (
                <Avatar src={hit.avatarUrl} size={25} />
              ) : (
                <HitIcon type={hit.recordType as string} />
              )}
            </ResultIconLeft>
            <ResultContentWrapper>
              <CustomHighlight hit={hit} attribute="only-important-for-types" />
            </ResultContentWrapper>
          </ResultLeft>
          <ResultRight>{enableKeyboardNav && <ReturnIcon />}</ResultRight>
        </StyledLink>
      </Link>
    </SearchResultsItem>
  );
};
